// Minimal concurrency limiter. Ensures at most `max` async tasks run in parallel.
// Keeps long-running Gemini generations from all firing at once and hitting 429s.
//
// .reset() drops every QUEUED-but-not-yet-started task (their promises stay
// pending forever — no .then/.catch will fire). In-flight tasks keep running
// to completion (we can't abort the HTTP request mid-flight), so callers
// MUST guard their .then handlers with a client-id check before applying any
// state — see Dashboard.jsx auto-gen effects. Without .reset(), switching
// clients while gens were queued would force the new client to wait for the
// old client's queue to drain before any of their own auto-gens could start,
// producing the "stuck, must refresh" glitch in the workspace.
export function createLimiter(max) {
  let active = 0;
  let queue = [];
  const next = () => {
    if (queue.length === 0 || active >= max) return;
    active += 1;
    const { fn, resolve, reject } = queue.shift();
    Promise.resolve()
      .then(fn)
      .then(
        (v) => { active -= 1; resolve(v); next(); },
        (e) => { active -= 1; reject(e); next(); }
      );
  };
  const enqueue = (fn) => new Promise((resolve, reject) => {
    queue.push({ fn, resolve, reject });
    next();
  });
  // Drop every queued task. Their callers' .then/.catch will never fire,
  // which is intentional — the per-client guard at the .then site is what
  // discards stale results, but dropping the queue prevents the new
  // client's auto-gens from being starved behind the old client's backlog.
  enqueue.reset = () => {
    queue = [];
  };
  return enqueue;
}

// Retry a promise-returning function. Tight limits to avoid billable Gemini calls stacking:
//   · default `retries=1` (i.e. MAX 2 attempts total) for automatic background generation
//   · pass `retries=0` for manual retries (user clicked "Tap to retry") so there is
//     exactly ONE API call per click and the user is in full control of spend
export async function withRetry(fn, { retries = 1, baseDelayMs = 1200 } = {}) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      // Don't retry auth/validation errors — those won't succeed on retry
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw err;
      }
      if (attempt === retries) break;
      // Exponential backoff with jitter so parallel retries from the same burst
      // don't all wake up together and immediately re-hit Gemini's rate limit.
      const delay = baseDelayMs * Math.pow(2, attempt) * (0.85 + Math.random() * 0.3);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
}
